import { ContentKind } from '@mg/shared';
import { splitTrailingHashtags } from './previewRules';

/**
 * What the thread preview draws, worked out from the composer's blocks and kept apart from the
 * markup (ThreadPreview.tsx) so that it can be tested on its own: which blocks are one post, what
 * each post says, and which pictures it shows.
 *
 * A thread post is drawn from its own blocks alone. Nothing stands in for a text that is not there
 * - the post's title is never sent in a thread, so it is never shown in one.
 */

/**
 * The part of a composer block the thread preview reads. Declared here rather than imported, so
 * that the preview does not depend on the composer: the composer's own EditorBlock fits it as is.
 */
export interface ThreadPreviewBlock {
  key: string;
  kind: ContentKind;
  text: string;
  assetId: string | null;
  assetUrl: string | null;
  assetPosterUrl: string | null;
  assetThumbUrl?: string | null;
  /** The thread post this block belongs to. Consecutive blocks with the same one are one post. */
  threadKey?: string;
}

/** One picture of a post: an image, or the still frame that stands for its video. */
export interface ThreadPreviewPicture {
  /** The block's key: a picture keeps it wherever its post moves, so it is not fetched again. */
  key: string;
  /** Where to load it from; null for a video with no still frame yet. */
  url: string | null;
  video: boolean;
}

export interface ThreadPreviewPost {
  /** The post's `threadKey`. */
  key: string;
  /** The post's text without the hashtags that end it - or those hashtags, when it is only them. */
  text: string;
  /** The hashtags that end the post; set on a line of their own. */
  hashtags: string[];
  /** What the post shows: its video alone, or its first images. */
  pictures: ThreadPreviewPicture[];
  /** How many more images the post holds than `pictures` shows. */
  more: number;
  /** The post has neither text nor media. */
  empty: boolean;
}

/** The most pictures one post shows. The last cell says how many more there are. */
export const THREAD_PREVIEW_MAX_CELLS = 4;

/**
 * A block that holds a file. The same test the thread's own rules apply (`mediaCompositionOf`): a
 * tile with no file is a placeholder, which is neither saved nor sent - so it is not drawn either.
 */
const filled = (block: ThreadPreviewBlock) => Boolean(block.assetId);

/** The posts of a thread, in order, from its blocks in the order the composer holds them. */
export function threadPreviewPosts(blocks: readonly ThreadPreviewBlock[]): ThreadPreviewPost[] {
  const runs: ThreadPreviewBlock[][] = [];
  for (const block of blocks) {
    const run = runs[runs.length - 1];
    if (run && run[0].threadKey === block.threadKey) run.push(block);
    else runs.push([block]);
  }
  return runs.map(previewPost);
}

function previewPost(blocks: ThreadPreviewBlock[]): ThreadPreviewPost {
  // The first text block is the post's text, as it is for the publisher.
  const body = blocks.find((b) => b.kind === ContentKind.TEXT)?.text.trim() ?? '';
  const { text, hashtags } = splitTrailingHashtags(body);
  // A post of nothing but hashtags is set as the text itself, as the single card sets it: there is
  // nothing for them to follow onto a line of their own.
  const tagsOnly = text === '' && hashtags.length > 0;

  // A video is the whole attachment of its post, shown as its still frame - the only part of a
  // video an <img> can show. Otherwise the images, in order.
  const video = blocks.find((b) => b.kind === ContentKind.VIDEO && filled(b));
  const images = blocks.filter((b) => b.kind === ContentKind.IMAGE && filled(b));
  const pictures: ThreadPreviewPicture[] = video
    ? [{ key: video.key, url: video.assetPosterUrl, video: true }]
    : images.slice(0, THREAD_PREVIEW_MAX_CELLS).map((b) => ({
        key: b.key,
        // The small copy whenever there is one, a post's only picture included: a thread can hold
        // fifty posts, and their originals are megabytes each.
        url: b.assetThumbUrl ?? b.assetUrl,
        video: false,
      }));

  return {
    // Every block of a thread carries its post's key; a list that is not a thread is one post.
    key: blocks[0].threadKey ?? blocks[0].key,
    text: tagsOnly ? hashtags.join(' ') : text,
    hashtags: tagsOnly ? [] : hashtags,
    pictures,
    more: video ? 0 : images.length - pictures.length,
    empty: body === '' && !blocks.some((b) => b.kind !== ContentKind.TEXT && filled(b)),
  };
}
